import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import { useNavigate } from "react-router-dom";
import useApi from "../../../hooks/useApi";
import { PAGE_PATH } from "../../../path";
import api from "../../../services/api";
import { consts } from "../../../shim/jsbridge";
import * as notifications from "../../../notifications/notifications";
import { convertBytesToMB, getSizeInBytes } from "../../../utils";
import LoadingAndErrorHandling from "../../commons/LoadingAndErrorHandling";
import useGetRulesetTestResults from "../hooks/useGetRulesetTestResults";
import useYaraEditorReducer from "./useYaraEditorReducer";
import YaraConfirmModal from "./YaraConfirmModal";
import YaraEditor from "./YaraEditor";
import YaraEditorModal from "./YaraEditorModal";
import YaraHeaderControls from "./YaraHeaderControls";
import YaraHistoryPanel from "./YaraHistoryPanel";
import YaraInfoPanel from "./YaraInfoPanel";
import YaraRulesetNameInput from "./YaraRulesetNameInput";
import {
  YARA_DATA,
  YARA_REVISION_HISTORY_FOR_RULESET,
  YARA_SAMPLES,
  YARA_REVISION_DETAILS
} from "../queryKeys";
import { useBrowserTitle } from "../../../hooks/useBrowserTitle";
import { PAGE_TITLE } from "../../../titles";
import { Tooltip } from "../../../components/tooltip";

const { data_upload_max_memory_size } = consts;

const showNotification = (msg, level = "error") => {
  notifications.show({
    sticky: false,
    level,
    message: msg
  });
};
const showNonEditorErrors = (errors = []) => {
  errors.forEach(err => showNotification(err));
};

const handleTooLargeContent = (contentSizeInBytes, callback) => {
  const limitSizeInMB = convertBytesToMB(data_upload_max_memory_size);
  const overflow = contentSizeInBytes - data_upload_max_memory_size;
  callback({
    show: true,
    msg: `Limit is ${limitSizeInMB} ${"megabyte".pluralize(
      limitSizeInMB
    )}. You are over the limit by ${overflow} ${"character".pluralize(
      overflow
    )}`
  });
};

const YaraEditorWrapper = ({
  id,
  canUserPublishToSdm = false,
  show = false,
  onClose,
  onSaveAndClose,
  onRunNewTestClick,
  onRetestClick
}) => {
  const [editorDirty, setEditorDirty] = useState(false);
  const [modalState, actions] = useYaraEditorReducer(id);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [toastData, setToastData] = useState({
    show: false,
    msg: ""
  });
  const queryClient = useQueryClient();
  const hideMainModal = rulesetId =>
    onClose(rulesetId || modalState.activeRuleId);
  const hideConfirmModal = () => setShowConfirmModal(false);
  const resetToast = () =>
    setToastData(prevState => ({
      ...prevState,
      show: false
    }));

  const editorRef = useRef();
  const inputRuleRef = useRef();
  const { latest } = useGetRulesetTestResults(modalState.activeRuleId);

  const navigate = useNavigate();

  const isEditMode = useMemo(() => !!modalState.ruleId, [modalState.ruleId]);
  const hasInputErrors = useMemo(
    () =>
      !modalState.rulesetName ||
      modalState.rulesetNameError ||
      modalState.isEditorEmpty,
    [
      modalState.rulesetName,
      modalState.rulesetNameError,
      modalState.isEditorEmpty
    ]
  );
  const [fetchResp, loading, error] = useApi(
    api.yaraFetchRule,
    { id: modalState.ruleId },
    [modalState.ruleId, modalState.savesPassed],
    {
      respMapper: resp => {
        actions.ruleFetch(resp);
        return resp;
      },
      isDisabled: !isEditMode
    }
  );

  const processInvalidResponse = respData => {
    const { errors, message } = respData;
    actions.saveError(errors);
    setToastData({ show: true, msg: "Saving failed!" });
    showNonEditorErrors(errors);
    showNotification(message);
    return false;
  };
  const processValidResponse = respData => {
    actions.ruleSaved(respData);
    setToastData({ show: true, msg: "Saved successfully!" });
    return true;
  };
  const processResponse = response => {
    const respData = response.data;
    if (!respData.success) {
      showNotification(respData.message);
      setToastData({ show: true, msg: "Saving failed!" });
      return false;
    }
    return respData.valid
      ? processValidResponse(respData)
      : processInvalidResponse(respData);
  };

  const saveRuleset = (publish = false, onSaveSuccess = _rulesetId => null) => {
    const content = editorRef.current.getValue();
    const contentSizeInBytes = getSizeInBytes(content);
    if (contentSizeInBytes > data_upload_max_memory_size) {
      handleTooLargeContent(contentSizeInBytes, setToastData);
      return;
    }

    const data = {
      name: modalState.rulesetName,
      content,
      enabled: modalState.enableRuleset,
      ticloud: modalState.enableRulesetTiCloud,
      publish
    };

    const formData = new URLSearchParams();
    // we don't send the rulesetName on update since it cannot be changed on edit page
    if (!isEditMode) {
      formData.append("name", data.name);
    }
    if (canUserPublishToSdm) {
      formData.append("publish", data.publish);
    }
    formData.append("content", data.content);
    formData.append("enabled", data.enabled);
    formData.append("ticloud", data.ticloud);

    setToastData({ show: true, msg: "Saving..." });

    const invalidateYaraData = () => {
      queryClient.invalidateQueries({ queryKey: [YARA_DATA] });
      queryClient.invalidateQueries({ queryKey: [YARA_SAMPLES] });
      queryClient.invalidateQueries({
        queryKey: [YARA_REVISION_HISTORY_FOR_RULESET]
      });
      queryClient.invalidateQueries({ queryKey: [YARA_REVISION_DETAILS] });
    };

    if (isEditMode) {
      api
        .yaraUpdateRule({ data: formData, id: modalState.ruleId })
        .then(
          response =>
            processResponse(response) &&
            onSaveSuccess(response.data?.details?.id)
        )
        .then(invalidateYaraData)
        .catch(() => setToastData({ show: true, msg: "Updating failed!" }));
    } else {
      api
        .yaraSaveRule(formData)
        .then(
          response =>
            processResponse(response) &&
            onSaveSuccess(response.data?.details?.id)
        )
        .then(invalidateYaraData)
        .catch(() => setToastData({ show: true, msg: "Saving failed!" }));
    }
  };

  const saveEnabled = useMemo(
    () =>
      !hasInputErrors &&
      (!isEditMode || modalState.needsSave || fetchResp?.details?.always_save),
    [
      isEditMode,
      hasInputErrors,
      modalState.needsSave,
      fetchResp?.details?.always_save
    ]
  );

  const handleRunNewTestClick = () => {
    navigate(
      `${PAGE_PATH.YARA}${PAGE_PATH.YARA_TESTRUN.replace(
        ":rulesetId",
        modalState.activeRuleId
      )}`
    );
    onRunNewTestClick?.();
  };
  const handleRetestClick = () => {
    const path = `${PAGE_PATH.YARA}${PAGE_PATH.YARA_TESTRUN_REANALYZE.replace(
      ":rulesetId",
      modalState.activeRuleId
    ).replace(":testrunId", latest.id.toString())}`;
    navigate(`${path}?retest=1`);
    onRetestClick?.();
  };

  // disabled retest button if there is no previous testrun or new editor value hasn't been saved
  const retestDisabled = !latest || modalState.needsSave || !editorDirty;

  useBrowserTitle(PAGE_TITLE.YARA_RULESET_EDIT(fetchResp?.details?.name), true);

  return (
    <YaraEditorModal
      title={"Yara Ruleset Editor"}
      className={"yara-modal-zindex"}
      backdropClassName={"yara-modal-zindex"}
      fullscreen
      show={show}
      onHide={() => {
        if (fetchResp?.details?.from_core) {
          hideMainModal();
          return;
        }
        if (modalState.needsSave) {
          setShowConfirmModal(true);
          return;
        }
        hideMainModal();
      }}
    >
      <LoadingAndErrorHandling error={error} loading={loading}>
        <div className={"yara-editor-container"}>
          <YaraHeaderControls
            canUserPublishToSdm={canUserPublishToSdm}
            isCoreRuleset={fetchResp?.details?.from_core}
            saveEnabled={saveEnabled}
            enableRuleset={modalState.enableRuleset}
            enableRulesetTiCloud={modalState.enableRulesetTiCloud}
            enableAutoCloudRetro={modalState.initAutoCloudRetro}
            onEnableRulesetClick={val => {
              actions.rulesetEnable(val);
            }}
            onEnableRulesetTiCloudClick={val => {
              actions.rulesetCloudEnable(val);
            }}
            onSaveClick={saveRuleset}
            onSaveCloseClick={() => {
              saveRuleset(true, rulesetId => {
                hideMainModal(rulesetId);
                onSaveAndClose(rulesetId);
              });
            }}
          />
          {isEditMode && (
            <YaraInfoPanel
              ruleId={modalState.ruleId}
              infoActive={fetchResp?.details?.info_active}
              infoAdded={fetchResp?.details?.info_added}
              infoEdited={fetchResp?.details?.info_edited}
            />
          )}
          <div className="flex width-100-pc flex-space-between">
            <YaraRulesetNameInput
              disabled={isEditMode}
              rulesetName={modalState.rulesetName}
              onChange={(val, hasError) => {
                actions.rulesetNameChanged(val, hasError);
              }}
              ref={inputRuleRef}
            />
            <div style={{ marginTop: "3px" }} className="align-center flex">
              <Button
                style={{ textTransform: "capitalize" }}
                variant="outline-dark"
                disabled={!modalState.activeRuleId}
                onClick={handleRunNewTestClick}
              >
                Run New Test
              </Button>
              <Tooltip
                disabled={!retestDisabled}
                content="Save changes before retesting."
                placement="top"
                id="retestTooltip"
              >
                <Button
                  disabled={retestDisabled}
                  style={{ textTransform: "capitalize", marginLeft: "10px" }}
                  onClick={handleRetestClick}
                >
                  Retest
                </Button>
              </Tooltip>
            </div>
          </div>
          {isEditMode && (
            <YaraHistoryPanel
              selectedRuleId={modalState.ruleId}
              historyItems={fetchResp?.history}
              onItemClick={ruleId => {
                setEditorDirty(true);
                actions.ruleSelected(ruleId);
              }}
            />
          )}
          <YaraEditor
            error={modalState.editorError}
            warnings={modalState.warnings}
            content={modalState.initEditorValue}
            readOnly={fetchResp?.details?.is_editing_disabled}
            ref={editorRef}
            onChange={value => {
              setEditorDirty(true);
              actions.editorValueChanged(value);
            }}
          />
        </div>
      </LoadingAndErrorHandling>

      <YaraConfirmModal
        isEditMode={isEditMode}
        show={showConfirmModal}
        rulesetName={modalState.rulesetName}
        onHide={() => hideConfirmModal()}
        onClickNo={() => {
          hideConfirmModal();
          hideMainModal();
        }}
        onClickYes={() => {
          hideConfirmModal();
          saveRuleset(false, rulesetId => {
            hideMainModal(rulesetId);
            onSaveAndClose(rulesetId);
          });
        }}
        onClickCancel={() => hideConfirmModal()}
      />

      <ToastContainer className="p-3" position="top-center">
        <Toast
          show={!!toastData.show}
          onClose={resetToast}
          autohide
          delay={2000}
          className="yara-toast"
        >
          <Toast.Body>{toastData.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </YaraEditorModal>
  );
};

YaraEditorWrapper.propTypes = {
  id: PropTypes.number,
  canUserPublishToSdm: PropTypes.bool,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveAndClose: PropTypes.func,
  onRunNewTestClick: PropTypes.func,
  onRetestClick: PropTypes.func
};

export default YaraEditorWrapper;
